import React, { useContext, useMemo } from 'react'
import { useAuthContext } from '../context/auth.context'
import { useApiListRolePermission } from '../api-new/state/content'
import { useLoad } from '../shared/hooks/load'

export const RoleContext = React.createContext()

export const useRoleContext = () => useContext(RoleContext)

export const RoleProvider = ({ children }) => {
  const authContext = useAuthContext()

  const apiListRolePermissionSys = useApiListRolePermission()
  const apiListRolePermissionOrg = useApiListRolePermission()

  // The restricted restore session (delete organization) reaches only the
  // restore page's routes: it reads no permissions; after the restore drops
  // the claim, the same user's are read.
  const restricted = !!authContext.state.orgPendingDeletion

  useLoad(() => !restricted && authContext.state.loginID && apiListRolePermissionSys.get(`${authContext.state.loginID}/login`), [authContext.state.loginID, restricted])
  useLoad(() => !restricted && authContext.state.userID && apiListRolePermissionOrg.get(`${authContext.state.userID}/user`), [authContext.state.userID, restricted])

  const sysRoles = useMemo(
    () => {
      let roles = {}
      apiListRolePermissionSys.items.forEach(role => (roles[role.identifier] = role))
      return roles
    },
    [apiListRolePermissionSys.items]
  )

  const orgRoles = useMemo(
    () => {
      let roles = {}
      apiListRolePermissionOrg.items.forEach(role => (roles[role.identifier] = role))
      return roles
    },
    [apiListRolePermissionOrg.items]
  )

  return (
    <RoleContext.Provider value={{ orgRoles, sysRoles, orgLoaded: !!apiListRolePermissionOrg.loaded, sysLoaded: !!apiListRolePermissionSys.loaded }}>
      {children}
    </RoleContext.Provider>
  )
}

export const ROLE_SYS = {
  SYS_DASHBOARD: 'SYS_DASHBOARD',
  SYS_APP: 'SYS_APP',
  SYS_ORGANIZATION: 'SYS_ORGANIZATION',
  SYS_LOGIN: 'SYS_LOGIN',
  SYS_AUTH_STATUS: 'SYS_AUTH_STATUS',
  SYS_PROVIDER: 'SYS_PROVIDER',
  SYS_SENDGRID_EVENT: 'SYS_SENDGRID_EVENT',
  // Request log (audit-trail phase 3): every API request, super admins only.
  SYS_REQUEST_LOG: 'SYS_REQUEST_LOG',
  // Audit log (audit-trail phase 5): every organization's entries, and the
  // retention settings. The page itself follows /audit/capabilities; these
  // only decide the sidebar entries.
  SYS_AUDIT_LOG: 'SYS_AUDIT_LOG',
  SYS_AUDIT_RETENTION: 'SYS_AUDIT_RETENTION',
  // Error tracking: the Errors page (read; update triages), and the alert
  // routing page (read shows an error's alerts on the Errors page).
  SYS_ERRORS: 'SYS_ERRORS',
  SYS_ALERT_ROUTING: 'SYS_ALERT_ROUTING',
  // LMS connector presets: the platform templates every organization's
  // connection is built from (read opens the pages; the server decides each
  // write).
  SYS_LMS_PRESET: 'SYS_LMS_PRESET',
  // API key oversight (Phase D; the server's RBAC.ScopeSysApiKey): read =
  // every organization's keys, their usage and its export; update = an
  // organization's API access switch and a key's rate limit; delete =
  // revoke any key.
  SYS_API_KEY: 'SYS_API_KEY',
  // Notifications (notify platform, C-6 §4): the staff Notifications
  // section (read: every ops view, health, captures, credential metadata;
  // update: replay, resend, park, drain, test-send, credential test;
  // create: add and rotate a push credential; delete: disable one).
  SYS_NOTIFY: 'SYS_NOTIFY',
  // AI tools telemetry (spec 2026-10-20 §9; core RBAC.ScopeSysAiToolsTelemetry,
  // contract §2.1): read = the staff page /admin/ai-tools (sessions,
  // postmortems and findings with their processing marks); update = change
  // a mark. Never create or delete: the telemetry itself can't be changed.
  SYS_AI_TOOLS_TELEMETRY: 'SYS_AI_TOOLS_TELEMETRY',
  // Packages (EducationXR's Unity package registry; core RBAC.ScopeSysPackages):
  // read = the staff section /admin/packages (packages, versions, publish
  // keys' metadata); create = add a package; update = deprecate or
  // undeprecate a version and create or rotate a publish key; delete =
  // delete a version and revoke a key.
  SYS_PACKAGES: 'SYS_PACKAGES',
  // Billing (core RBAC.ScopeSysBilling): read = the staff section
  // /admin/billing (the plan catalog and an organization's entitlements);
  // update = recompute an organization's stored entitlements.
  SYS_BILLING: 'SYS_BILLING',
  // Website consent log (core RBAC.ScopeSysConsentLog): read = the staff
  // section /admin/consent-log (look up a consent ID, verify a signed one,
  // export a date range as CSV).
  SYS_CONSENT_LOG: 'SYS_CONSENT_LOG'
}

export const ROLE_ORG = {
  DASHBOARD: 'DASHBOARD',
  NODE: 'NODE',
  USER: 'USER',
  GROUP: 'GROUP',
  GROUP_USER: 'GROUP_USER',
  NODE_USER: 'NODE_USER',
  NODE_GROUP: 'NODE_GROUP',
  // HEATMAP: 'HEATMAP',
  ANALYTIC: 'ANALYTIC',
  ASSESSMENT_MANAGE: 'ASSESSMENT_MANAGE',
  ASSESSMENT_REPORT: 'ASSESSMENT_REPORT',
  TRADESHOW: 'TRADESHOW',
  SELF_REGISTER: 'SELF_REGISTER',
  DOC: 'DOC',
  // The organization's own single sign-on (the Authentication page): the
  // server's RBAC.ScopeOrgSSO, deliberately separate from USER (spec 7).
  SSO: 'ORG_SSO',
  // The organization itself (delete-organization spec §2.4): read = the
  // Organization page and the deletion status (owners and admins), delete =
  // schedule the deletion, update = restore it (owners only).
  ORGANIZATION: 'ORG_ORGANIZATION',
  // The organization's Audit log (audit-trail phase 5; the server's
  // RBAC.ScopeOrgAuditLog). The sidebar entry only: the page follows
  // /audit/capabilities.
  AUDIT_LOG: 'ORG_AUDIT_LOG',
  // The LMS connector's organization scope (the server's RBAC.ScopeOrgLms).
  LMS: 'LMS',
  // The organization's API keys (Phase D; the server's RBAC.ScopeOrgApiKey):
  // read = the keys, their usage and request logs; create = a new key;
  // update = rename, change the role, rotate; delete = revoke.
  API_KEY: 'API_KEY'
}
